import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Heart,
  Sparkles,
  MapPin,
  ChevronRight,
  PawPrint
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessStories = ({ 
  region = null,  // Optional region filter for future regionalization
  limit = 5,
  showHeader = true,
  className = ""
}) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, [region]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', limit);
      if (region) {
        params.append('region', region);
      }
      
      const response = await axios.get(`${API_URL}/api/adopter-cards/recognitions/recent?${params.toString()}`);
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching success stories:', error);
      // Show placeholder stories if API fails
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getPetEmoji = (petType) => {
    const emojis = {
      dog: '🐕',
      cat: '🐱',
      bird: '🐦',
      rabbit: '🐰',
      other: '🐾'
    };
    return emojis[petType?.toLowerCase()] || '🐾';
  };

  if (loading) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary fill-primary" />
              Success Stories
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-3/4 mb-1" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (stories.length === 0) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary fill-primary" />
              Success Stories
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className="text-center py-6">
          <PawPrint className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Be the first to adopt and share your story!
          </p>
          <Link to="/adopt" className="mt-3 inline-block">
            <Button variant="outline" size="sm">
              Find a Pet
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="success-stories">
      {showHeader && (
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary fill-primary" />
              Success Stories
            </span>
            <Link to="/adopt">
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div className="space-y-4">
          {stories.map((story, idx) => (
            <div 
              key={story.id || idx} 
              className="flex items-start gap-3 group"
              data-testid={`success-story-${idx}`}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-lg">
                {getPetEmoji(story.pet_type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-medium">{story.pet_name}</span>
                  {' '}found their forever home with{' '}
                  <span className="font-medium">{story.adopter_first_name}</span>!
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                  <span>{story.agency_name}</span>
                  <span>•</span>
                  <span>{getTimeAgo(story.created_at)}</span>
                </div>
              </div>
              <Sparkles className="w-4 h-4 text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
        
        {/* Region indicator for future use */}
        {region && (
          <div className="mt-4 pt-3 border-t">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              Showing stories from {region}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SuccessStories;
